import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { Autocomplete, Checkbox, TextField, Chip, Typography, CircularProgress, Box } from '@mui/material';

import { setSelectedCampaignList } from '../../redux/advertiser/advertiserActions';

// reset functions
import { resetScreenData } from '../../redux/campaign/campaignActions';
import { resetReportData } from '../../redux/reporting/reportingActions';
import { resetCreativeData } from '../../redux/creative/creativeActions';
import { resetWowData } from '../../redux/wow/wowActions';
import { resetIAPData } from '../../redux/iap/iapActions';

const CampaignAutocompleteDropDown = ({
  campaign_list,
  campaign_list_rest,
  setSelectedCampaignList,
  advertiser_campaign_fetching,
  campaign_list_error_msg,
  performanceLoading,
  widzardLoading,
  reportLoading,
  isSkan
}) => {
  const dispatch = useDispatch();

  const [value, setValue] = useState([]);
  const [disableCampaignDropDown, setDisableCampaignDropDown] = useState(false);

  // 🔹 Dynamic label
  const getLabel = () => {
    if (advertiser_campaign_fetching) return 'Loading campaigns...';
    if (campaign_list.length === 0) return 'No campaigns available';
    return 'Select Campaigns';
  };

  // 🔹 Handle chip delete
  const handleDelete = (deletedOption) => {
    const newValue = value.filter((option) => option.campaign_guid !== deletedOption.campaign_guid);
    setValue(newValue);
    const campaignIds = newValue.map((v) => v.campaign_guid);
    setSelectedCampaignList(campaignIds);
  };

  // 🔹 Debounced setter
  const debouncedApiCall = debounce((campaignIds) => {
    setSelectedCampaignList(campaignIds);
  }, 150);

  const setCampaigns = (newValue) => {
    const campaignIds = newValue.map((v) => v.campaign_guid);
    setValue(newValue);
    debouncedApiCall(campaignIds);
  };

  // 🔹 Disable dropdown while fetching
  useEffect(() => {
    setDisableCampaignDropDown(advertiser_campaign_fetching);
  }, [advertiser_campaign_fetching, performanceLoading, widzardLoading, reportLoading]);

  // 🔹 Auto-select all campaigns after fetching completes
  useEffect(() => {
    if (!advertiser_campaign_fetching && campaign_list.length > 0) {
      const uniqueCampaigns = campaign_list.filter(
        (campaign, index, self) => index === self.findIndex((c) => c.campaign_guid === campaign.campaign_guid)
      );
      setValue(uniqueCampaigns);
      const campaignIds = uniqueCampaigns.map((c) => c.campaign_guid);
      setSelectedCampaignList(campaignIds);
    }

    // Reset if campaign list cleared
    if (campaign_list_rest || campaign_list.length === 0) {
      setValue([]);
      setSelectedCampaignList([]);
    }
  }, [advertiser_campaign_fetching, campaign_list, campaign_list_rest]);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Autocomplete
        multiple
        id="campaign-dropdown"
        options={[...new Map(campaign_list.map((c) => [c.campaign_guid, c])).values()]}
        disableCloseOnSelect
        disabled={disableCampaignDropDown}
        loading={advertiser_campaign_fetching}
        value={value}
        getOptionLabel={(option) => option.campaign_name}
        onChange={(event, newValue) => {
          // reset all dependent data before updating
          dispatch(resetScreenData());
          dispatch(resetReportData());
          dispatch(resetCreativeData());
          dispatch(resetWowData());
          dispatch(resetIAPData());
          setCampaigns(newValue);
        }}
        isOptionEqualToValue={(option, val) => option.campaign_guid === val.campaign_guid}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            {option.campaign_name}
          </li>
        )}
        renderTags={(tagValue, getTagProps) => (
          <>
            {tagValue.slice(0, 2).map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.campaign_guid}
                label={option.campaign_name}
                onDelete={() => handleDelete(option)}
              />
            ))}
            {tagValue.length > 2 && <span>{`+${tagValue.length - 2} more`}</span>}
          </>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={getLabel()}
            variant="outlined"
            error={!advertiser_campaign_fetching && !!campaign_list_error_msg}
            helperText={!advertiser_campaign_fetching ? campaign_list_error_msg : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {advertiser_campaign_fetching && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
        )}
      />

      {/* 🔹 Optional subtle overlay spinner */}
      {advertiser_campaign_fetching && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.6)',
            borderRadius: '8px',
            zIndex: 2
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}

      {/* 🔹 Dynamic status message */}
      {(() => {
        if (campaign_list.length === 0 && !campaign_list_error_msg) {
          return (
            <Typography variant="body2" sx={{ color: 'primary.main', mt: 1, fontSize: '0.9rem' }}>
              Loading campaigns, please wait...
            </Typography>
          );
        }

        if (value.length === 0 && !campaign_list_error_msg) {
          return (
            <Typography variant="h6" sx={{ color: 'red', mt: 1, fontSize: '0.9rem' }}>
              Please select some campaigns from the above dropdown.
            </Typography>
          );
        }

        return null;
      })()}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  campaign_list: state.advertiserReducer.campaign_list,
  campaign_list_rest: state.advertiserReducer.campaign_list_rest,
  advertiser_campaign_fetching: state.advertiserReducer.advertiser_campaign_fetching,
  performanceLoading: state.campaignReducer.performanceLoading,
  widzardLoading: state.campaignReducer.widzardLoading,
  reportLoading: state.campaignReducer.reportLoading,
  isSkan: state.campaignReducer.isSkan,
  campaign_list_error_msg: state.advertiserReducer.campaign_list_error_msg
});

const mapDispatchToProps = {
  setSelectedCampaignList
};

export default connect(mapStateToProps, mapDispatchToProps)(CampaignAutocompleteDropDown);
